import React, {
  useEffect,
  useState,
} from 'react';

import {
  collection,
  doc,
  getCountFromServer,
  getDocs,
  getFirestore,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';

import { useAuth } from '../contexts/AuthContext';
import firebase from '../firebase';
import {
  Button,
  Card,
  Input,
  Typography,
} from './common';

interface SessionInfo {
  id: string; // Firestore doc id
  code: string;
  createdAt: any;
  sessionId: string;
  numAthletes: number;
  numCoaches: number;
}

export default function SessionsList() {
  const { currentUser } = useAuth();
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newCode, setNewCode] = useState('');
  const [saving, setSaving] = useState(false);

  const db = getFirestore(firebase);

  useEffect(() => {
    async function fetchSessions() {
      if (!currentUser) return;
      setLoading(true);
      setError('');
      try {
        // Get sessions created by current user
        const sessionsRef = collection(db, 'sessions');
        const q = query(sessionsRef, where('createdBy', '==', currentUser.uid));
        const querySnapshot = await getDocs(q);
        const sessionList: SessionInfo[] = [];
        for (const docSnap of querySnapshot.docs) {
          const data = docSnap.data();

          const athletesCountSnap = await getCountFromServer(collection(db, `sessions/${docSnap.id}/athletes`));
          const coachesCountSnap = await getCountFromServer(collection(db, `sessions/${docSnap.id}/coaches`));
          sessionList.push({
            id: docSnap.id,
            code: data.code,
            createdAt: data.createdAt,
            sessionId: data.sessionId,
            numAthletes: athletesCountSnap.data().count,
            numCoaches: coachesCountSnap.data().count,
          });
        }
        setSessions(sessionList);
      } catch (err) {
        setError('Failed to load sessions');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchSessions();
  }, [currentUser, db]);

  const handleEdit = (id: string, currentCode: string) => {
    setEditingId(id);
    setNewCode(currentCode);
  };

  const handleSave = async (id: string) => {
    setSaving(true);
    setError('');
    try {
      const sessionRef = doc(db, 'sessions', id);
      await updateDoc(sessionRef, { code: newCode.toUpperCase() });
      setSessions(sessions.map(s => s.id === id ? { ...s, code: newCode.toUpperCase() } : s));
      setEditingId(null);
    } catch (err) {
      setError('Failed to update code');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
        <Typography variant="h2">My Sessions</Typography>
        {loading && <Typography>Loading...</Typography>}
        {error && <div className="error-message">{error}</div>}
        {!loading && sessions.length === 0 && <Typography>No sessions found.</Typography>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
          {sessions.map(session => (
            <Card key={session.id} className="session-card">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Typography variant="body"><b>Session ID:</b> {session.id}</Typography>
                <Typography variant="body"><b>Created:</b> {session.createdAt?.toDate ? session.createdAt.toDate().toLocaleString() : ''}</Typography>
                <Typography variant="body"><b>Athletes:</b> {session.numAthletes}</Typography>
                <Typography variant="body"><b>Coaches:</b> {session.numCoaches}</Typography>
                <div style={{ display: 'block', alignItems: 'center', gap: 8 }}>
                  <Typography variant="body"><b>Code:</b></Typography>
                </div>
                {editingId === session.id ? (
                    <>
                      <Input
                        value={newCode}
                        onChange={e => {
                          const value = e.target.value.toUpperCase().slice(0, 6);
                          setNewCode(value);
                        }}
                        className="session-code-input"
                      />
                      <Button onClick={() => handleSave(session.id)} disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                      </Button>
                      <Button variant="secondary" onClick={() => setEditingId(null)} disabled={saving}>
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      <span style={{ fontFamily: 'monospace', fontSize: 18 }}>{session.code}</span>
                      <Button variant="secondary" onClick={() => handleEdit(session.id, session.code)}>
                        Edit
                      </Button>
                    </>
                  )}
              </div>
            </Card>
          ))}
        </div>
    </div>
  );
} 